
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, cubicBezier } from 'framer-motion';
import { UploadCloudIcon, ClockIcon, DiamondIcon, LayersIcon, SparklesIcon, UsersIcon } from '../components/icons';
import { useTranslation } from '../i18n/useTranslation';
import { AppPage, UserProfile } from '../types';
import { Compare } from '../components/ui/compare';
import { HERO_IMAGES } from '../constants';
import { CDN_URLS, OCI_FOLDERS } from '../config';
import { homeService } from '../services/homeService';
import Footer from '../components/Footer';
import DoubleCarousel from '../components/DoubleCarousel';
import StyleSwipeWidget from '../components/StyleSwipeWidget';
import { useSEO } from '../hooks/useSEO';
import { useAppContext } from '../AppContext';
import StickyCTA from '../components/StickyCTA';


interface HomePageProps {
    onPhotoUpload: (file: File) => void;
    onNavigate: (page: AppPage, params?: Record<string, string>) => void;
    onGenerateModel?: () => void;
    onModelFinalized?: (modelUrl: string, detectedProfile?: Partial<UserProfile>) => void;
    isModelLoaded: boolean;
    isLoading?: boolean;
    loadingMessage?: string;
}

const coreValues = [
    {
        icon: ClockIcon,
        titleKey: 'homePage_vision_ai_agents_title',
        descKey: 'homePage_vision_ai_agents_desc'
    },
    {
        icon: DiamondIcon,
        titleKey: 'homePage_vision_quality_title',
        descKey: 'homePage_vision_quality_desc'
    },
    {
        icon: LayersIcon,
        titleKey: 'homePage_vision_digitalTwin_title',
        descKey: 'homePage_vision_digitalTwin_desc'
    }
];

const HomePage: React.FC<HomePageProps> = ({ onPhotoUpload, onNavigate, onGenerateModel, isModelLoaded, isLoading, loadingMessage }) => {
    const { t } = useTranslation();
    const { currentUserId } = useAppContext();


    useSEO(
        t('home_seo_title'),
        t('home_seo_desc'),
        t('home_seo_keywords').split(',').map(kw => kw.trim())
    );


    const [heroImages, setHeroImages] = useState({ first: '', second: '' });

    useEffect(() => {
        let mounted = true;
        homeService.getSelectedHeroImages().then(images => {
            if (mounted) setHeroImages(images);
        });
        return () => { mounted = false; };
    }, []);
    const [showSticky, setShowSticky] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleScroll = () => {
            if (containerRef.current) {
                setShowSticky(containerRef.current.scrollTop > 500);
            }
        };
        const currentContainer = containerRef.current;
        currentContainer?.addEventListener('scroll', handleScroll);
        return () => currentContainer?.removeEventListener('scroll', handleScroll);
    }, []);


    // Hero image rotation logic removed as per redesign


    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            if (file.type.startsWith('image/')) {
                onPhotoUpload(file);
            } else {
                alert(t('startScreen_uploadErrorFileTypeError'));
            }
        }
    };

    const handleMainCta = () => {
        if (isModelLoaded) {
            onNavigate('style-lab', { mode: 'fitting' });
        } else {
            document.getElementById('photo-upload-input')?.click();
        }
    };

    const pageVariants = {
        initial: { opacity: 0 },
        animate: { opacity: 1, transition: { duration: 0.8, staggerChildren: 0.2 } },
        exit: { opacity: 0, transition: { duration: 0.4 } },
    };

    const itemVariants = {
        initial: { opacity: 0, y: 30 },
        animate: { opacity: 1, y: 0, transition: { duration: 0.8, ease: cubicBezier(0.2, 0.8, 0.2, 1) } },
    };

    return (
        <motion.div
            key="home-page"
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            ref={containerRef}
            className="h-full w-full bg-white dark:bg-[#18181B] overflow-x-hidden overflow-y-auto custom-scrollbar"
        >
            <input
                type="file"
                id="photo-upload-input"
                className="hidden"
                accept="image/*"
                onChange={handleFileChange}
                aria-label={t('homePage_uploadInput_label')}
            />

            {/* Hero Section */}
            {/* Changed height from h-screen to h-full to fit within the main layout area without overflow */}
            {/* Hero Section */}
            <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden clip-path-hero-1">
                {/* Background Image with Overlay */}
                <div className="absolute inset-0 z-0">
                    <div className="absolute inset-x-0 top-0 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80" aria-hidden="true">
                        <div className="relative left-[calc(50%-11rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-[#ff80b5] to-[#9089fc] opacity-20 sm:left-[calc(50%-30rem)] sm:w-[72.1875rem] clip-path-hero-gradient-1" />
                    </div>
                </div>

                <div className="mx-auto max-w-7xl px-6 py-20 lg:py-32 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        {/* Left Column: Text & CTAs */}
                        <div className="text-center lg:text-left">
                            <motion.h1
                                variants={itemVariants}
                                className="text-4xl md:text-6xl font-serif font-bold text-gray-900 dark:text-[#F1F1F1] mb-6 leading-tight"
                            >
                                {t('home_hero_title_new')}
                            </motion.h1>
                            <motion.p
                                variants={itemVariants}
                                className="mt-6 text-lg md:text-xl leading-relaxed text-gray-600 dark:text-[#F1F1F1]/80 max-w-2xl mx-auto lg:mx-0"
                            >
                                {t('home_hero_subtitle')}
                            </motion.p>
                            <motion.div
                                variants={itemVariants}
                                className="mt-10 flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4"
                            >
                                <button
                                    id="onboarding-upload-button"
                                    onClick={handleMainCta}
                                    className="relative inline-flex items-center justify-center px-8 py-4 text-base font-bold text-white bg-indigo-600 rounded-full cursor-pointer hover:bg-indigo-700 hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-indigo-500/30"
                                >
                                    {isModelLoaded ? <LayersIcon className="w-5 h-5 mr-2" /> : <UploadCloudIcon className="w-5 h-5 mr-2" />}
                                    {isModelLoaded ? t('nav_dressingRoom') : t('homePage_cta')}
                                </button>
                                {!isModelLoaded && (
                                    <button
                                        onClick={handleMainCta}
                                        className="relative inline-flex items-center justify-center px-8 py-4 text-base font-bold text-indigo-600 bg-indigo-50 border border-indigo-100 rounded-full cursor-pointer hover:bg-indigo-100 hover:scale-105 transition-all duration-300"
                                    >
                                        <SparklesIcon className="w-5 h-5 mr-2" />
                                        {t('home_hero_cta')}
                                    </button>
                                )}
                            </motion.div>

                            {/* Loading Feedback */}
                            {isLoading && loadingMessage && (
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="mt-6 flex items-center justify-center lg:justify-start gap-3 text-indigo-600 dark:text-indigo-400"
                                >
                                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-indigo-600 border-t-transparent dark:border-indigo-400"></div>
                                    <span className="text-sm font-medium">{loadingMessage}</span>
                                </motion.div>
                            )}

                            <motion.div variants={itemVariants} className="mt-8 flex flex-wrap justify-center lg:justify-start gap-4 text-sm font-medium text-gray-500 dark:text-gray-400">
                                <a 
                                    href="/styling-advisor?agent=styleQuestionnaire"
                                    onClick={(e) => { e.preventDefault(); onNavigate('styling-advisor', { agent: 'styleQuestionnaire' }); }} 
                                    className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors flex items-center gap-1 cursor-pointer"
                                >
                                    {t('homePage_solveStyleProblem_link')} <span aria-hidden="true">&rarr;</span>
                                </a>
                                <a 
                                    href="/booking"
                                    onClick={(e) => { e.preventDefault(); onNavigate('booking'); }} 
                                    className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors flex items-center gap-1 cursor-pointer"
                                >
                                    {t('homePage_bookSession_link')} <span aria-hidden="true">&rarr;</span>
                                </a>
                                <a 
                                    href="/booking?service=business-workshops"
                                    onClick={(e) => { e.preventDefault(); onNavigate('booking', { service: 'business-workshops' }); }} 
                                    className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors flex items-center gap-1 cursor-pointer"
                                >
                                    {t('homePage_forCompanies_link')} <span aria-hidden="true">&rarr;</span>
                                </a>
                            </motion.div>
                        </div>

                        {/* Right Column: Compare Visual */}
                        <motion.div
                            variants={itemVariants}
                            className="relative lg:h-[600px] w-full flex items-center justify-center"
                        >
                            <div className="relative p-2 sm:p-4 bg-white/50 dark:bg-[#2A2A2A]/50 backdrop-blur-sm rounded-3xl shadow-xl border border-white/50 dark:border-[#C0A172]/20 w-full max-w-[450px] aspect-[3/4] rotate-2 hover:rotate-0 transition-transform duration-500">
                                <Compare
                                    firstImage={heroImages.first}
                                    secondImage={heroImages.second}
                                    slideMode="drag"
                                    className="w-full h-full rounded-2xl bg-gray-200 dark:bg-gray-700"
                                />
                                <div className="absolute -bottom-6 -right-6 -z-10 bg-indigo-600/10 dark:bg-indigo-400/10 w-full h-full rounded-3xl blur-xl"></div>
                            </div>
                        </motion.div>
                    </div>
                </div>

                <div className="absolute inset-x-0 top-[calc(100%-13rem)] -z-10 transform-gpu overflow-hidden blur-3xl sm:top-[calc(100%-30rem)]" aria-hidden="true">
                    <div className="relative left-[calc(50%+3rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 bg-gradient-to-tr from-[#ff80b5] to-[#9089fc] opacity-20 sm:left-[calc(50%+36rem)] sm:w-[72.1875rem] clip-path-hero-gradient-2" />
                </div>
            </section>

            {/* Style Swipe Widget Section - Gamification */}
            {
                !isModelLoaded && !currentUserId?.includes('guest') && (
                    <div className="py-12 bg-white dark:bg-[#18181B] border-b border-gray-100 dark:border-[#C0A172]/10">
                        <div className="mx-auto max-w-7xl px-6 lg:px-8">
                            <div className="flex flex-col md:flex-row items-center gap-12">
                                <div className="md:w-1/2 text-center md:text-left">
                                    <span className="text-indigo-600 dark:text-[#C0A172] font-bold tracking-wider uppercase text-sm mb-2 block">{t('styleSwipe_section_label') || "Personalize Your AI"}</span>
                                    <h2 className="text-3xl font-serif font-bold text-gray-900 dark:text-[#F1F1F1] mb-4">{t('styleSwipe_section_title') || "Fine-tune your style profile"}</h2>
                                    <p className="text-lg text-gray-600 dark:text-[#F1F1F1]/70 mb-8 leading-relaxed">
                                        {t('styleSwipe_section_desc') || "Swipe right on looks you love to help your AI stylist understand your unique taste better. The more you swipe, the better your recommendations."}
                                    </p>
                                </div>
                                <div className="md:w-1/2 w-full flex justify-center">
                                    <StyleSwipeWidget />
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Empathy Section */}
            <div className="py-16 sm:py-20 bg-white dark:bg-[#18181B]">
                <div className="mx-auto max-w-7xl px-6 lg:px-8">
                    <div className="mx-auto max-w-2xl text-center">
                        <motion.h2
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            className="text-3xl font-serif font-bold tracking-tight text-gray-900 dark:text-[#C0A172] sm:text-4xl px-4"
                        >
                            {t('home_empathy_title')}
                        </motion.h2>
                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.1 }}
                            className="mt-6 text-lg leading-8 text-gray-600 dark:text-[#F1F1F1]/70"
                        >
                            {t('home_empathy_desc')}
                        </motion.p>
                    </div>
                </div>
            </div>

            {/* Double Carousel Section */}
            <DoubleCarousel />

            {/* Press Bar */}
            <div className="py-12 bg-white dark:bg-[#18181B] border-y border-gray-100 dark:border-[#C0A172]/10">
                <div className="mx-auto max-w-7xl px-6 lg:px-8">
                    <p className="text-center text-xs font-bold tracking-[0.3em] uppercase text-gray-400 dark:text-[#C0A172] mb-10">{t('home_press_title')}</p>
                    <div className="flex flex-wrap justify-center items-center gap-x-16 gap-y-10 opacity-60 dark:opacity-60 grayscale hover:grayscale-0 transition-all duration-500 dark:text-[#F1F1F1]">
                        <span className="text-2xl font-serif font-bold tracking-tighter">VOGUE</span>
                        <span className="text-2xl font-sans font-black">ELLE</span>
                        <span className="text-2xl font-serif italic font-medium">Harper's BAZAAR</span>
                        <span className="text-2xl font-sans font-bold uppercase tracking-widest">Glamour</span>
                        <span className="text-2xl font-serif font-bold">FashionUnited</span>
                    </div>
                </div>
            </div>

            {/* How It Works Section */}
            <div className="py-16 sm:py-20 bg-indigo-50 dark:bg-indigo-900/10">
                <div className="mx-auto max-w-7xl px-6 lg:px-8">
                    <div className="mx-auto max-w-2xl text-center mb-16">
                        <h2 className="text-base font-bold leading-7 text-indigo-600 dark:text-indigo-400 uppercase tracking-[0.2em] mb-4">{t('home_how_it_works_title')}</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                        {[
                            { step: 1, icon: UploadCloudIcon, color: 'text-blue-500' },
                            { step: 2, icon: SparklesIcon, color: 'text-indigo-500' },
                            { step: 3, icon: UsersIcon, color: 'text-purple-500' }
                        ].map((item, idx) => (
                            <motion.div
                                key={item.step}
                                initial={{ opacity: 0, x: -20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.2 }}
                                className="relative flex flex-col items-center text-center group"
                            >
                                <div className={`w-20 h-20 rounded-2xl bg-white dark:bg-[#2A2A2A] shadow-lg flex items-center justify-center mb-8 border border-gray-100 dark:border-[#C0A172]/20 group-hover:scale-110 transition-transform duration-300 ${item.color}`}>
                                    <item.icon className="w-10 h-10" />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 dark:text-[#F1F1F1] mb-4">{t(`home_how_it_works_step${item.step}_title` as any)}</h3>
                                <p className="text-gray-600 dark:text-[#F1F1F1]/60 leading-relaxed px-4">{t(`home_how_it_works_step${item.step}_desc` as any)}</p>
                                {idx < 2 && (
                                    <div className="hidden md:block absolute top-10 -right-6 w-12 h-[2px] bg-indigo-100 dark:bg-indigo-800" />
                                )}
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Vision Section */}
            <div className="bg-gray-50 dark:bg-[#18181B] py-16 sm:py-20 border-t border-gray-100 dark:border-[#C0A172]/10">
                <div className="mx-auto max-w-7xl px-6 lg:px-8">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.5 }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                        className="mx-auto max-w-2xl text-center"
                    >
                        <h2 className="text-sm font-bold tracking-[0.2em] uppercase text-indigo-600 dark:text-[#C0A172] mb-4">{t('homePage_visionTitle')}</h2>
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ staggerChildren: 0.2 }}
                        className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none"
                    >
                        <dl className="grid max-w-xl grid-cols-1 gap-x-12 gap-y-16 lg:max-w-none lg:grid-cols-3">
                            {coreValues.map((value) => (
                                <motion.div
                                    key={value.titleKey}
                                    variants={itemVariants}
                                    className="relative flex flex-col items-center text-center p-8 bg-white dark:bg-[#2A2A2A] rounded-3xl shadow-sm border border-gray-100 dark:border-[#C0A172]/10 hover:shadow-xl transition-shadow duration-300"
                                >
                                    <dt className="text-xl font-bold leading-7 text-gray-900 dark:text-[#F1F1F1] mb-4">
                                        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-indigo-50 dark:bg-[#C0A172]/10 text-indigo-600 dark:text-[#C0A172] mb-6">
                                            <value.icon className="h-8 w-8" aria-hidden="true" />
                                        </div>
                                        {t(value.titleKey)}
                                    </dt>
                                    <dd className="mt-1 text-base leading-7 text-gray-600 dark:text-[#F1F1F1]/60">{t(value.descKey)}</dd>
                                </motion.div>
                            ))}
                        </dl>
                    </motion.div>
                </div>
            </div>
            <Footer onNavigate={onNavigate} />
            <StickyCTA
                show={showSticky}
                label={isModelLoaded ? t('nav_dressingRoom') : t('homePage_cta')}
                onClick={handleMainCta}
                icon={isModelLoaded ? <LayersIcon className="w-5 h-5" /> : <UploadCloudIcon className="w-5 h-5" />}
            />
        </motion.div >
    );
};

export default HomePage;
